import dayjs from "dayjs";
import { actionDate } from "utils/actionDate";
import { getTaskLabelIds, sortLabels } from "utils/actionLabels";

export const PRIORITY_OPTIONS = [
  { value: 2, label: "High" },
  { value: 1, label: "Medium" },
  { value: 0, label: "Low" },
];

export const EFFORT_OPTIONS = [
  { value: 2, label: "High" },
  { value: 1, label: "Medium" },
  { value: 0, label: "Low" },
];

export const GROUP_BY_OPTIONS = [
  { value: "due", label: "Due date" },
  { value: "priority", label: "Priority" },
  { value: "label", label: "Label" },
  { value: "none", label: "No grouping" },
];

export const SORT_BY_OPTIONS = [
  { value: "due", label: "Due date" },
  { value: "priority", label: "Priority" },
  { value: "effort", label: "Effort" },
  { value: "newest", label: "Newest first" },
];

const levelName = (value) =>
  PRIORITY_OPTIONS.find((option) => option.value === toLevel(value))?.label ||
  "Medium";

// Old documents store priority and effort as strings.
export const toLevel = (value) => {
  const level = parseInt(value, 10);
  return Number.isNaN(level) ? 1 : level;
};

export const getPriorityName = (task) => levelName(task?.priority);
export const getEffortName = (task) => levelName(task?.effort);

// Whole days from today to the due date. Negative means overdue.
export const getDueDayOffset = (dueValue, today = new Date()) => {
  const due = actionDate(dueValue);
  if (!due) return null;
  return dayjs(due).startOf("day").diff(dayjs(today).startOf("day"), "day");
};

// overdue, today, week (the next 6 days), later, or none.
export const getDueBucket = (dueValue, today = new Date()) => {
  const offset = getDueDayOffset(dueValue, today);
  if (offset === null) return "none";
  if (offset < 0) return "overdue";
  if (offset === 0) return "today";
  if (offset < 7) return "week";
  return "later";
};

// The pill colour for a due date. Completed actions are never overdue.
export const getDueTone = (task, today = new Date()) => {
  if (task?.completed) return "later";
  const bucket = getDueBucket(task?.due_date, today);
  if (bucket === "overdue") return "over";
  if (bucket === "today") return "today";
  if (bucket === "week") return "soon";
  return "later";
};

// "Yesterday", "Today", "Tomorrow", then "Mon 28 Sep". The short form is for
// the narrow workspace panel: "Mon" inside this week, "1 Oct" after it.
export const formatDueLabel = (dueValue, today = new Date(), short = false) => {
  const due = actionDate(dueValue);
  if (!due) return "No date";
  const offset = getDueDayOffset(due, today);
  if (offset === -1) return "Yesterday";
  if (offset === 0) return "Today";
  if (offset === 1) return "Tomorrow";
  if (short) {
    return offset > 1 && offset < 7
      ? dayjs(due).format("ddd")
      : dayjs(due).format("D MMM");
  }
  return dayjs(due).format("ddd D MMM");
};

const dueTime = (task) => actionDate(task?.due_date)?.getTime() ?? Infinity;
const createdTime = (task) => actionDate(task?.created_on)?.getTime() ?? 0;

const byDue = (a, b) =>
  dueTime(a) - dueTime(b) || toLevel(b.priority) - toLevel(a.priority);

const SORTERS = {
  due: byDue,
  priority: (a, b) => toLevel(b.priority) - toLevel(a.priority) || byDue(a, b),
  effort: (a, b) => toLevel(b.effort) - toLevel(a.effort) || byDue(a, b),
  newest: (a, b) => createdTime(b) - createdTime(a),
};

export const sortActionItems = (items, sortBy = "due") =>
  items.slice().sort(SORTERS[sortBy] || SORTERS.due);

const DUE_GROUPS = [
  { key: "overdue", name: "Overdue", tone: "over" },
  { key: "today", name: "Today", tone: "today" },
  { key: "week", name: "This week", tone: "soon" },
  { key: "later", name: "Later", tone: "later" },
  { key: "none", name: "No date", tone: "later" },
  // Completed actions whose due date has passed. They are not overdue.
  { key: "earlier", name: "Earlier", tone: "later" },
];

const PANEL_DUE_GROUPS = [
  { key: "overdue", name: "Overdue", tone: "over" },
  { key: "today", name: "Today", tone: "today" },
  { key: "upcoming", name: "Upcoming", tone: "later" },
  { key: "none", name: "No date", tone: "later" },
  { key: "earlier", name: "Earlier", tone: "later" },
];

const groupByDue = (items, today, panel) => {
  const definitions = panel ? PANEL_DUE_GROUPS : DUE_GROUPS;
  const buckets = Object.fromEntries(definitions.map((group) => [group.key, []]));
  items.forEach((task) => {
    // A completed action is not overdue any more.
    let bucket = getDueBucket(task.due_date, today);
    if (task.completed && bucket === "overdue") bucket = "earlier";
    if (panel && (bucket === "week" || bucket === "later")) bucket = "upcoming";
    buckets[bucket].push(task);
  });
  // Completed actions read newest first, so older pages of history go in at
  // the bottom instead of between rows the person is looking at.
  if (buckets.earlier) {
    buckets.earlier.sort(
      (a, b) => (completedAt(b)?.getTime() || 0) - (completedAt(a)?.getTime() || 0)
    );
  }
  return definitions.map((group) => ({ ...group, items: buckets[group.key] }));
};

// When the action was completed. Older actions completed before
// completed_updated_at existed fall back to their due date.
const completedAt = (task) =>
  actionDate(task.completed_updated_at) || actionDate(task.due_date);

const COMPLETED_GROUPS = [
  { key: "done-today", name: "Completed today", tone: "today" },
  { key: "done-week", name: "Last 7 days", tone: "soon" },
  { key: "done-earlier", name: "Earlier", tone: "later" },
  { key: "done-none", name: "No date", tone: "later" },
];

// For the Completed tab: newest first, grouped by completion day.
const groupByCompletion = (items, today) => {
  const buckets = Object.fromEntries(COMPLETED_GROUPS.map((group) => [group.key, []]));
  const newestFirst = items
    .slice()
    .sort((a, b) => (completedAt(b)?.getTime() || 0) - (completedAt(a)?.getTime() || 0));
  newestFirst.forEach((task) => {
    const offset = getDueDayOffset(completedAt(task), today);
    if (offset === null) buckets["done-none"].push(task);
    else if (offset >= 0) buckets["done-today"].push(task);
    else if (offset > -7) buckets["done-week"].push(task);
    else buckets["done-earlier"].push(task);
  });
  return COMPLETED_GROUPS.map((group) => ({ ...group, items: buckets[group.key] }));
};

const groupByPriority = (items) =>
  PRIORITY_OPTIONS.map((option) => ({
    key: `priority-${option.value}`,
    name: option.label,
    tone: option.label.toLowerCase(),
    items: items.filter((task) => toLevel(task.priority) === option.value),
  }));

// An action with two labels shows in both groups.
const groupByLabel = (items, labels) => {
  const knownIds = new Set(labels.map((label) => label.id));
  const labelGroups = sortLabels(labels).map((label) => ({
    key: `label-${label.id}`,
    name: label.name,
    tone: "label",
    color: label.color,
    items: items.filter((task) => getTaskLabelIds(task).includes(label.id)),
  }));
  const unlabelled = items.filter(
    (task) => !getTaskLabelIds(task).some((id) => knownIds.has(id))
  );
  return [
    ...labelGroups,
    { key: "label-none", name: "No label", tone: "later", items: unlabelled },
  ];
};

// Moves the given actions into a "Just added" group at the top, newest
// first, so a new action shows next to the composer.
export const withJustAddedGroup = (groups, recentItems) => {
  if (!recentItems.length) return groups;
  const recentIds = new Set(recentItems.map((task) => task.id));
  const rest = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((task) => !recentIds.has(task.id)),
    }))
    .filter((group) => group.items.length > 0);
  return [{ key: "just-added", name: "Just added", tone: "today", items: recentItems }, ...rest];
};

// Returns [{ key, name, tone, color?, items }] without empty groups. Items
// inside each group follow sortBy.
export const groupActionItems = (
  items,
  groupBy = "due",
  { today = new Date(), labels = [], sortBy = "due", panel = false, completedTab = false } = {}
) => {
  const sorted = sortActionItems(items, sortBy);
  let groups;
  if (completedTab && groupBy === "due") groups = groupByCompletion(items, today);
  else if (groupBy === "priority") groups = groupByPriority(sorted);
  else if (groupBy === "label") groups = groupByLabel(sorted, labels);
  else if (groupBy === "none")
    groups = [{ key: "all", name: "All actions", tone: "later", items: sorted }];
  else groups = groupByDue(sorted, today, panel);

  return groups.filter((group) => group.items.length > 0);
};
